package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span

/**
 * Styles for the expanding icon component.
 * Provides smooth CSS transition animation for the rotation effect.
 */
object ExpandingIconStyles : StyleSheet() {
    /**
     * Base style for the expanding icon container.
     * Sets up the CSS transition for smooth rotation animation.
     */
    val expandingIcon by style {
        display(DisplayStyle.InlineBlock)
        textAlign("center")
        property("line-height", "0")
        overflow("hidden")
        property("transition", "transform 0.2s ease")
    }

    /**
     * Applied when the icon is in the expanded state.
     * Rotates the icon 90 degrees clockwise.
     */
    val expanded by style {
        property("transform", "rotate(90deg)")
    }
}

/**
 * Configuration options for the expanding icon component.
 */
class ExpandingIconConfiguration {
    /** The size of the icon. Defaults to 14px to match typical navigation use cases. */
    var size: CSSNumeric = 14.px

    /** Optional tooltip text displayed on hover. */
    var title: String? = null

    internal var onToggle: (() -> Unit)? = null

    /**
     * Sets the callback to be invoked when the icon is clicked.
     * Use this to toggle the expanded state in your component.
     *
     * @param block The callback function to execute on click
     */
    fun onToggle(block: () -> Unit) {
        this.onToggle = block
    }
}

/**
 * An animated chevron icon that rotates to indicate expanded/collapsed state.
 *
 * This component renders a chevron-right icon that smoothly rotates 90 degrees
 * when transitioning between collapsed and expanded states. It's designed for
 * use in expandable sections, tree views, accordion panels, and similar UI patterns.
 *
 * The animation uses CSS transitions for smooth, performant rotation that works
 * well across all browsers.
 *
 * @param expanded Whether the icon should be in the expanded (rotated) state
 * @param init Optional configuration block for customizing size, title, and click behavior
 *
 * Example usage:
 * ```kotlin
 * // Basic usage with external state management
 * var isExpanded by remember { mutableStateOf(false) }
 *
 * expandingIcon(isExpanded) {
 *     onToggle { isExpanded = !isExpanded }
 * }
 *
 * // With custom size
 * expandingIcon(isExpanded) {
 *     size = 20.px
 *     title = "Toggle section"
 *     onToggle { isExpanded = !isExpanded }
 * }
 *
 * // Read-only (no click handler) - icon just reflects state
 * expandingIcon(isExpanded)
 * ```
 */
@Composable
fun expandingIcon(expanded: Boolean, init: ExpandingIconConfiguration.() -> Unit = {}) {
    val config = ExpandingIconConfiguration().apply(init)

    Style(ExpandingIconStyles)

    Span({
        classes(ExpandingIconStyles.expandingIcon)
        if (expanded) {
            classes(ExpandingIconStyles.expanded)
        }

        config.title?.let { title(it) }

        style {
            // Make the container square so rotation doesn't shift position
            width(config.size)
            height(config.size)
            if (config.onToggle != null) {
                cursor("pointer")
            }
        }

        if (config.onToggle != null) {
            onClick {
                it.stopPropagation()
                config.onToggle?.invoke()
            }
        }
    }) {
        svgIcon(SVGIcons.chevronRight) {
            size = config.size
        }
    }
}
